import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
//import Heading from '@tiptap/extension-heading';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import HelpTooltip from '../HelpTooltip';
import FieldLock from '../FieldLock';
import IconButton from '@mui/material/IconButton';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { styled } from '@mui/material/styles';
import { BiHelpCircle } from 'react-icons/bi';

interface RichTextFieldProps {
  labelText: string;
  labelId: string;
  mandatory?: boolean;
  helpTextTitle?: string;
  helpTextBody?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  value: string;
  disabled?: boolean;
  locked?: boolean;
  onLock?: (field: string, locked: boolean) => void;
  disabledLock?: boolean;
  displayedLock?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  maxWords?: number;
  onWordCountChange?: (count: number) => void;
}

// Styled editor area for sizing and list style
const StyledEditorContent = styled(EditorContent)(({ theme }) => ({
  minHeight: 80,
  width: '100%',
  maxWidth: '100%',
  fontSize: 16,
  fontFamily: theme.typography.fontFamily,
  outline: 'none',
  boxSizing: 'border-box',
  '& .ProseMirror': {
    minHeight: 150,
    width: '100%',
    maxWidth: '100%',
    padding: theme.spacing(2),
    boxSizing: 'border-box',
    outline: 'none',
  },
  '& ol': {
    listStyleType: 'decimal',
    marginLeft: theme.spacing(3),
  },
  '& ul': {
    listStyleType: 'disc',
    marginLeft: theme.spacing(3),
  },
  '& ul ul': {
    listStyleType: 'circle',
  },
  '& ul ul ul': {
    listStyleType: 'square',
  },
  '& ol ol': {
    listStyleType: 'lower-alpha',
  },
  '& ol ol ol': {
    listStyleType: 'lower-roman',
  },
}));

const RichTextField: React.FC<RichTextFieldProps> = ({
  labelText,
  labelId,
  mandatory,
  helpTextTitle,
  helpTextBody,
  onChange,
  onBlur,
  value,
  disabled,
  locked,
  onLock,
  disabledLock = false,
  displayedLock = true,
  error,
  helperText,
  fullWidth,
  maxWords,
  onWordCountChange,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const dirtyHtml = editor.getHTML();
      const cleanHtml = DOMPurify.sanitize(dirtyHtml, {
        ALLOWED_TAGS: ['p', 'ul', 'ol', 'li', 'b', 'i', 'u', 'strong', 'em'],
        ALLOWED_ATTR: []
      });
      onChange(cleanHtml);
    },
    onBlur: () => {
      if (onBlur) onBlur();
    },
  });
 React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  // Toolbar actions
  const setBold = () => editor?.chain().focus().toggleBold().run();
  const setItalic = () => editor?.chain().focus().toggleItalic().run();
  const setUnderline = () => editor?.chain().focus().toggleUnderline().run();
  const setOrderedList = () => editor?.chain().focus().toggleOrderedList().run();
  const setBulletList = () => editor?.chain().focus().toggleBulletList().run();
  const setHeading = (level: 1 | 2 | null) => {
    if (level) {
      editor?.chain().focus().setHeading({ level: level as 1 | 2 }).run();
    } else {
      editor?.chain().focus().setParagraph().run();
    }
  };

  // Calculate word count from editor content
  const wordCount = useMemo(() => {
    if (!editor) return 0;
    const text = editor.getText();
    return text.trim().split(/\s+/).filter(Boolean).length;
  }, [editor?.getText()]);

  React.useEffect(() => {
    if (onWordCountChange) {
      onWordCountChange(wordCount);
    }
  }, [wordCount, onWordCountChange]);

  const maxWordLimit = maxWords || 1000;
  const showWordWarning = wordCount > maxWordLimit;

  return (
    <Box
      display="flex"
      flexDirection="column"
      width={fullWidth ? '100%' : '100%'}
      maxWidth={980}
      data-testid={labelId}
    >
      <FormLabel component="legend" sx={{ fontSize: '12px', mb: 0.1, display: 'flex', alignItems: 'center' }} htmlFor={labelId}>
        {displayedLock && (
          <FieldLock
            labelId={labelId}
            value={!!locked}
            disabled={!!disabledLock}
            displayed={displayedLock}
            onChange={locked => onLock ? onLock(labelId, locked) : null}
            size={14}
          />
        )}
        <span style={{ marginRight: 4 }}>
          {labelText}
          {mandatory && <span style={{ color: 'inherit', marginLeft: 4, fontSize: '16px' }}>*</span>}
        </span>
        {helpTextTitle && helpTextBody && (
          <HelpTooltip title={helpTextTitle} body={helpTextBody} ><BiHelpCircle/></HelpTooltip>
        )}
      </FormLabel>
      <Box
        sx={{
          background: '#fff',
          borderRadius: 2,
          border: '1px solid #ccc',
          minHeight: 150,
          mt: 0.5,
           transition: 'box-shadow 0.2s, border-color 0.2s',
          '& .ProseMirror-focused': {
            boxShadow: '0 0 0 2px #1976d2',
            borderRadius:2,
            borderColor: '#1976d2',
          }
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            p: 1,
            borderRadius: '8px 8px 0 0',
            background: '#f9f9f9',
            borderBottom: '1px solid #e0e0e0',
            alignItems: 'center',
          }}
        >
          <select
            id={`${labelId}-heading-select`}
            name={`${labelId}-heading`}
            aria-label="Text formatting style"
            autoComplete="off"
            style={{ fontSize: 14, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', marginRight: 8 }}
            disabled={disabled}
            value={
              editor?.isActive('heading', { level: 1 }) ? 'h1'
                : editor?.isActive('heading', { level: 2 }) ? 'h2'
                : 'normal'
            }
            onChange={e => {
              if (e.target.value === 'h1') setHeading(1);
              else if (e.target.value === 'h2') setHeading(2);
              else setHeading(null);
            }}
          >
            <option value="normal">Normal</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
          </select>
          <IconButton onClick={setBold} disabled={disabled} size="large">
            <FormatBoldIcon fontSize="medium" />
          </IconButton>
          <IconButton onClick={setItalic} disabled={disabled} size="large">
            <FormatItalicIcon fontSize="medium" />
          </IconButton>
          <IconButton onClick={setUnderline} disabled={disabled} size="large">
            <FormatUnderlinedIcon fontSize="medium" />
          </IconButton>
          <IconButton onClick={setOrderedList} disabled={disabled} size="large">
            <FormatListNumberedIcon fontSize="medium" />
          </IconButton>
          <IconButton onClick={setBulletList} disabled={disabled} size="large">
            <FormatListBulletedIcon fontSize="medium" />
          </IconButton>
        </Box>
        {/* Editor */}
  <StyledEditorContent editor={editor} role="textbox" aria-label={labelText} data-name={labelId} />
      </Box>
      {/* Word count and warning */}
      <Box display="flex" alignItems="center" mt={0.5}>
        <Box fontSize={12} color={showWordWarning ? 'error.main' : 'text.secondary'}>
          {`Word count: ${wordCount}${showWordWarning ? ` (max ${maxWordLimit} exceeded)` : ''}`}
        </Box>
      </Box>
      {error && (
        <Box color="error.main" fontSize={12} mt={0.5}>{helperText}</Box>
      )}
    </Box>
  );
};

export default RichTextField;